/**
 * FAANG-grade refactored _app.tsx
 * This is the target architecture after completing all refactoring phases
 *
 * Original: 665 lines, monolithic component
 * Refactored: ~100 lines, modular architecture
 */

import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { useSelector } from 'react-redux';

// Feature flags for test infrastructure
import { AppProviders } from '../components/providers/AppProviders';
import { TestHarness } from '../components/test-harness/TestHarness';
import { FEATURES } from '../config/features';
// Custom hooks (Phase 2)
import { useAuthHydration } from '../hooks/useAuthHydration';
import { useLayoutRouter } from '../hooks/useLayoutRouter';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useServiceWorker } from '../hooks/useServiceWorker';
// Layout system (Phase 4)
// Provider composition (Phase 3)
import type { RootState } from '../store';

// Global store exposure for Week 6 harness (safe no-op on server)
if (typeof window !== 'undefined') {
  // This will be handled by the Redux provider in AppProviders
  // (window as any).store and (window as any).__REDUX_STORE__ are set there
  if (typeof (window as any).DEBUG_MODE === 'undefined') {
    (window as any).DEBUG_MODE = false;
  }
}

/**
 * Inner component that runs after providers are set up
 * This ensures Redux hooks work properly
 */
const AppContent: React.FC<{
  Component: AppProps['Component'];
  pageProps: AppProps['pageProps'];
}> = ({ Component, pageProps }) => {
  const router = useRouter();
  const user = useSelector((state: RootState) => state.auth.user);
  const preferredCatalogSlug = useSelector((state: RootState) => state.auth.preferredCatalogSlug);

  // ─── STALE CHUNK RECOVERY (deploy-safe) ───
  // When the server deploys new chunks while a user has the page open,
  // client-side navigation fails because the old chunk hashes don't exist.
  // This handler catches those errors and does a full page reload.
  React.useEffect(() => {
    const handleRouteError = (err: Error & { cancelled?: boolean }, url: string) => {
      if (err.cancelled) return; // User cancelled navigation — not an error
      const isChunkError =
        err.name === 'ChunkLoadError' ||
        err.message?.includes('Loading chunk') ||
        err.message?.includes('Failed to fetch dynamically imported module') ||
        err.message?.includes('ChunkLoadError');
      if (isChunkError) {
        const reloadKey = '__chunk_reload_attempted';
        if (!sessionStorage.getItem(reloadKey)) {
          console.warn('[_app] 🔄 Stale chunk on route change — reloading:', url);
          sessionStorage.setItem(reloadKey, '1');
          window.location.href = url; // Full navigation (not client-side)
          return;
        }
      }
    };
    router.events.on('routeChangeError', handleRouteError);
    // Clear the reload guard on successful navigation so future deploys can trigger it
    const clearReloadGuard = () => sessionStorage.removeItem('__chunk_reload_attempted');
    router.events.on('routeChangeComplete', clearReloadGuard);
    return () => {
      router.events.off('routeChangeError', handleRouteError);
      router.events.off('routeChangeComplete', clearReloadGuard);
    };
  }, [router]);

  // Custom hooks handle all complex logic (Phase 2)
  // These must run AFTER ReduxProvider is mounted
  useAuthHydration();
  useServiceWorker();
  useOfflineSync();

  // Preferred catalog is available in Redux but not automatically enforced
  // Users can visit any catalog they want - preferred catalog is just for convenience

  // Layout routing determines which layout to use (Phase 4)
  const { LayoutComponent } = useLayoutRouter();

  // Build canonical URL — uses NEXT_PUBLIC_SITE_URL env var (set per-environment).
  // Falls back to window.location.origin in dev so nothing is hardcoded.
  const siteOrigin =
    process.env.NEXT_PUBLIC_SITE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const canonicalUrl = siteOrigin
    ? `${siteOrigin}${router.asPath.split('?')[0]}`
    : null;

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        {/* SEO: Canonical URL — prevents www vs non-www duplicate content penalty */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>

      {/* Test harness only in testing environments (Phase 5) */}
      {FEATURES.TESTING_INFRASTRUCTURE && <TestHarness />}

      {/* Layout system - now stable and always enabled */}
      <LayoutComponent>
        <Component {...pageProps} />
      </LayoutComponent>
    </>
  );
};

export default function MyAppRefactored({ Component, pageProps, router }: AppProps) {
  return (
    <AppProviders>
      <AppContent Component={Component} pageProps={pageProps} />
    </AppProviders>
  );
}
